/**
 * There is a smattering of force casting in this file. All of it is done to
 * circumvent the handling of generics with both React.memo and
 * styled-components.
 *
 * Being that we'd like to allow flexibility with the items leveraged in our
 * Omnibar, but have existing functionality built on the assumption it's always
 * set to ICommand, we set that to the default and otherwise expose a type
 * parameter for new consumers.
 */
import { Classes, MaybeElement } from "@blueprintjs/core";
import { Omnibar, OmnibarProps } from "@blueprintjs/select";
import React from "react";
import styled from "styled-components";

export interface ICommand {
  title: string;
  icon: MaybeElement;
  category: string;
  combo: string;
  mode: "all" | "command" | "edit";
  disabled: boolean;
  visible?: boolean;
  onAction: () => void;
}

export interface HexPortalOmnibarProps<T> extends OmnibarProps<T> {
  realClassName?: string;
}

/**
 * This component is an intermediate component that allows us to use
 * styled components with a class prop that isn't `className`.
 *
 * We basically just save off the consumer `className` to `realClassName`, applying the
 * styles added by styled components to `portalClassName`.
 */
const HexPortalOmnibar_ = <T = ICommand,>({
  className,
  overlayProps,
  realClassName,
  ...rest
}: HexPortalOmnibarProps<T>): JSX.Element => {
  return (
    <Omnibar<T>
      className={realClassName}
      overlayProps={{
        portalClassName: className,
        transitionDuration: 50,
        ...overlayProps,
      }}
      {...rest}
    />
  );
};

const OMNIBAR_WIDTH = 360;

const StyledHexPortalOmnibar = styled(HexPortalOmnibar_)`
  && {
    > .${Classes.OVERLAY} {
      > .${Classes.OVERLAY_CONTENT} {
        left: calc(50% - ${OMNIBAR_WIDTH / 2}px);

        width: ${OMNIBAR_WIDTH}px;

        background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};
        box-shadow: ${({ theme }) => theme.boxShadow.DIALOG};

        filter: blur(0) !important;
      }

      .${Classes.INPUT} {
        color: ${({ theme }) => theme.fontColor.DEFAULT};
      }

      .${Classes.MENU} {
        color: ${({ theme }) => theme.fontColor.DEFAULT};

        box-shadow: inset 0 1px 0 ${({ theme }) => theme.borderColor.MUTED};
      }

      > .${Classes.OVERLAY_BACKDROP} {
        background: none;
      }
    }
  }
` as typeof HexPortalOmnibar_;

export const HexPortalOmnibar = React.memo(
  StyledHexPortalOmnibar,
) as typeof StyledHexPortalOmnibar;

export interface HexOmnibarProps<T> extends OmnibarProps<T> {}

const HexOmnibar_ = <T = ICommand,>({
  className,
  ...rest
}: OmnibarProps<T>): JSX.Element => (
  <StyledHexPortalOmnibar<T> realClassName={className} {...rest} />
);

export const HexOmnibar = React.memo(HexOmnibar_) as typeof HexOmnibar_;
